"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.getAngleBetweenEntities = getAngleBetweenEntities;
exports.getPointTangentOfEntity = getPointTangentOfEntity;
var _bSpline = require("./bSpline");
var _convertLineToArc = require("./convertLineToArc");
var _vector = require("./vector");
/**
 * Returns the point and tangent of the specified point in the entity
 * @param {Entity} entity The entity whose endpoint and tangent to return
 * @param {Number} t Whether to return the point at the start of the entity (0), the end of the entity (1), or somewhere in the middle
 * @param {String} [tiebreakDirection] When t falls on a point with a non-continuous derivative, such as a kink on a spline, tiebreakDirection specifies whether to return the derivative in the "forward" or "backward" direction.
 * @returns {Array} An array of two items, the endpoint and the tangent
 */
function getPointTangentOfEntity(entity, t, tiebreakDirection) {
  const e = entity;
  let p, tan;
  switch (e.type) {
    case 'ARC':
      let sa = e.startAngle;
      let ea = e.endAngle;
      while (ea < sa) ea += Math.PI * 2;
      let cos, sin;
      cos = Math.cos(sa * (1 - t) + ea * t);
      sin = Math.sin(sa * (1 - t) + ea * t);
      p = {
        x: e.x + e.r * cos,
        y: e.y + e.r * sin
      };
      tan = {
        x: -sin,
        y: cos
      };
      break;
    case 'LINE':
      p = (0, _vector.add)((0, _vector.mul)(e.start, 1 - t), (0, _vector.mul)(e.end, t));
      tan = (0, _vector.sub)(e.end, e.start);
      break;
    case 'CIRCLE':
      {
        let cos, sin;
        cos = Math.cos(t * 2 * Math.PI);
        sin = Math.sin(t * 2 * Math.PI);
        p = {
          x: e.x + e.r * cos,
          y: e.y + e.r * sin
        };
        tan = {
          x: -sin,
          y: cos
        };
        break;
      }
    case 'SPLINE':
      {
        // If we are near or at a knot without a continuous derivative, results may be unexpected.
        const controlPointsForLib = e.controlPoints.map(p => [p.x, p.y]);

        // Get the point and tangent
        let {
          point,
          derivative
        } = (0, _bSpline.bSplineDeriv)(t, e.degree, controlPointsForLib, e.knots, e.weights, tiebreakDirection);
        p = {
          x: point[0],
          y: point[1]
        };
        tan = {
          x: derivative[0],
          y: derivative[1]
        };

        // Compute derivative numerically to check
        let derivMargin = 1e-6;
        let tUnder = t - derivMargin;
        let tOver = t + derivMargin;
        // Adjust derivative window if near an edge (but don't change p itself)
        if (tiebreakDirection === 'forward') {
          tUnder = t;
          tOver = t + derivMargin * 2;
        }
        if (tiebreakDirection === 'backward') {
          tOver = t;
          tUnder = t - derivMargin * 2;
        }
        if (tUnder < 0) {
          tUnder = 0;
          t = derivMargin * 2;
        }
        if (tOver > 1) {
          tOver = 1;
          t = 1 - derivMargin * 2;
        }
        let pointUnder, pointOver;
        if (tUnder === t) {
          // Reuse point if possible
          pointUnder = point;
        } else {
          pointUnder = (0, _bSpline.bSpline)(tUnder, e.degree, controlPointsForLib, e.knots, e.weights);
        }
        if (tOver === t) {
          // Reuse point if possible
          pointOver = point;
        } else {
          pointOver = (0, _bSpline.bSpline)(tOver, e.degree, controlPointsForLib, e.knots, e.weights);
        }
        let h = tOver - tUnder;
        const pUnder = {
          x: pointUnder[0],
          y: pointUnder[1]
        };
        const pOver = {
          x: pointOver[0],
          y: pointOver[1]
        };
        let tanCheck = (0, _vector.mul)((0, _vector.sub)(pOver, pUnder), 1 / h);

        // Analytical and numerical derivatives might differ by a scale factor. I'm not sure why that is exactly. It might
        // depend on the number of kinks in the spline. But, it doesn't matter since the tangent only has to be tangent.
        let _cross = (0, _vector.cross)(tan, tanCheck);
        let _dot = (0, _vector.dot)(tan, tanCheck);
        let angle = Math.atan2(_cross, _dot);
        if (!(Math.abs(angle) < 1e-3)) {
          console.warn(`Directions of the analytical and numerical derivatives of spline differ by ${angle} radians at point ${JSON.stringify(p)}. tan = ${JSON.stringify(tan)}, tanCheck = ${JSON.stringify(tanCheck)}`);
        }
        break;
      }
    case 'ELLIPSE':
      {
        // Start with a unit circle, then multiply by the transformation matrix which is formed by the major axis, and a
        // vector perpendicular to that and scaled by axisRatio
        let angle = t * (e.endAngle - e.startAngle) + e.startAngle;

        // Transformation matrix, formed by the major and minor axes
        let M = [[e.majorX, -e.majorY * e.axisRatio], [e.majorY, e.majorX * e.axisRatio]];

        // Point on the unit circle
        let pUnit = {
          x: Math.cos(angle),
          y: Math.sin(angle)
        };

        // Tangent to the unit circle
        let tanUnit = {
          x: -pUnit.y,
          y: pUnit.x
        };

        // Rotated and scaled point
        p = {
          x: pUnit.x * M[0][0] + pUnit.y * M[0][1] + e.x,
          y: pUnit.x * M[1][0] + pUnit.y * M[1][1] + e.y
        };

        // Rotated and scaled tangent
        tan = {
          x: tanUnit.x * M[0][0] + tanUnit.y * M[0][1],
          y: tanUnit.x * M[1][0] + tanUnit.y * M[1][1]
        };
        break;
      }
    case 'POLYLINE':
    case 'LWPOLYLINE':
      {
        const verts = e.vertices.slice();
        if (e.closed) {
          verts.push(verts[0]);
        }
        if (verts.length === 1) {
          p = verts[0];
          tan = {
            x: 0,
            y: 0
          };
        } else {
          let i0 = Math.floor((verts.length - 1) * t);
          if (i0 < 0) i0 = 0;
          if (i0 > verts.length - 2) i0 = verts.length - 2;
          let i1 = i0 + 1;
          let tSeg = (t - i0 / (verts.length - 1)) * (verts.length - 1);
          let bulge = verts[i0].bulge;
          if (bulge != null && (bulge < 0 || bulge > 0) && (0, _vector.norm)((0, _vector.sub)(verts[i1], verts[i0])) > 1e-15) {
            let arc = (0, _convertLineToArc.convertLineToArc)(verts[i0], verts[i1], bulge);
            return getPointTangentOfEntity(arc, arc.reversed ? 1 - tSeg : tSeg);
          } else {
            p = (0, _vector.add)((0, _vector.mul)(verts[i0], 1 - tSeg), (0, _vector.mul)(verts[i1], tSeg));
            tan = (0, _vector.sub)(verts[i1], verts[i0]);
          }
        }
        break;
      }
    default:
      // console.log(e) 
      // @ts-expect-error - e.type should be never
      throw new Error('Unsupported entity: ' + e.type);
  }
  return [p, tan];
}

/**
 * 
 * @param {Entity} e0 
 * @param {*} e1 
 */
function getAngleBetweenEntities(e0, e1) {
  let [p0, tan0] = getPointTangentOfEntity(e0, e0.reversed ? 0 : 1);
  let [p1, tan1] = getPointTangentOfEntity(e1, e1.reversed ? 1 : 0);

  // Verify entities meet at same point
  if ((0, _vector.norm2)((0, _vector.sub)(p0, p1)) > 1e-6) {
    throw new Error('Cannot get angle between entities: entities do not meet at a point');
  }
  if (e0.reversed) tan0 = (0, _vector.mul)(tan0, -1);
  if (e1.reversed) tan1 = (0, _vector.mul)(tan1, -1);

  // Get signed angle between the two vectors.
  // Found this nifty trick at https://stackoverflow.com/questions/5188561/signed-angle-between-two-3d-vectors-with-same-origin-within-the-same-plane
  let _cross = (0, _vector.cross)(tan0, tan1);
  let _dot = (0, _vector.dot)(tan0, tan1);
  let angle = Math.atan2(_cross, _dot);
  return angle;
}